import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Badge, Button, ButtonGroup, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import ReactApexChart from 'react-apexcharts'
import { useNavigate } from 'react-router-dom'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { toISODate } from '../utils/thaiDate'

const PERIODS = [
  { key: 'daily',   label: 'รายวัน' },
  { key: 'weekly',  label: 'รายสัปดาห์' },
  { key: 'monthly', label: 'รายเดือน' },
  { key: 'yearly',  label: 'รายปี' },
]

const GENDERS = [
  { key: '',  label: 'ทั้งหมด', color: 'secondary' },
  { key: 'm', label: 'ชาย',     color: 'primary'   },
  { key: 'f', label: 'หญิง',    color: 'danger'    },
]

const fmt = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export default function BankDashboard() {
  const navigate = useNavigate()
  const [period,   setPeriod]   = useState('monthly')
  const [gender,   setGender]   = useState('')
  const [fromDate, setFromDate] = useState(null)
  const [toDate,   setToDate]   = useState(null)
  const [data,     setData]     = useState(null)
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState('')

  const today = toISODate(new Date())

  const fetchDashboard = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams({ date: today })
      if (gender) params.set('gender', gender)
      if (fromDate && toDate) {
        params.set('from_date', toISODate(fromDate))
        params.set('to_date',   toISODate(toDate))
      } else {
        params.set('period', period)
      }
      const res = await axiosInstance.get(`/api/bank/dashboard?${params}`)
      if (res.data.success) setData(res.data.data)
    } catch (err) {
      setError(err.response?.data?.message || 'โหลดข้อมูล dashboard ไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }, [today, period, gender, fromDate, toDate])

  useEffect(() => { fetchDashboard() }, [fetchDashboard])

  const barChart = useMemo(() => {
    const rows = data?.chart_data || []
    return {
      series: [
        { name: 'ฝาก',  data: rows.map(r => parseFloat(r.deposit    || 0)) },
        { name: 'ถอน',   data: rows.map(r => parseFloat(r.withdrawal || 0)) },
      ],
      options: {
        chart:  { type: 'bar', toolbar: { show: false } },
        colors: ['#198754', '#dc3545'],
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 3 } },
        dataLabels: { enabled: false },
        legend: { position: 'top', fontSize: '12px' },
        xaxis: {
          categories: rows.map(r => r.label),
          labels: { style: { fontSize: '11px' }, rotate: -30 },
        },
        yaxis: { labels: { style: { colors: '#6e7985', fontSize: '11px' } } },
        tooltip: { y: { formatter: (v) => `฿${fmt(v)}` } },
      },
    }
  }, [data])

  const donutChart = useMemo(() => {
    const dep = parseFloat(data?.total_deposit    || 0)
    const wit = parseFloat(data?.total_withdrawal || 0)
    return {
      series: [dep, wit],
      options: {
        chart:  { type: 'donut', toolbar: { show: false } },
        labels: ['ฝาก', 'ถอน'],
        colors: ['#198754', '#dc3545'],
        legend: { position: 'bottom', fontSize: '13px' },
        dataLabels: { enabled: true },
        tooltip: { y: { formatter: (v) => `฿${fmt(v)}` } },
      },
    }
  }, [data])

  const skin = document.documentElement.getAttribute('data-skin')
  const cardBg = skin === 'dark' ? 'dark' : 'white'

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <Container fluid>
          {/* Title bar */}
          <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
            <h4 className="main-title mb-0">
              <i className="ri-bank-line me-2 text-primary" />
              Dashboard ธนาคารนักเรียน
            </h4>
            <div className="d-flex align-items-center gap-3 flex-wrap">
              {/* Gender filter */}
              <div className="d-flex align-items-center gap-2">
                {GENDERS.map(g => (
                  <Form.Check
                    key={g.key}
                    type="radio"
                    id={`gender-${g.key || 'all'}`}
                    name="gender"
                    label={g.label}
                    checked={gender === g.key}
                    onChange={() => setGender(g.key)}
                    className="mb-0"
                  />
                ))}
              </div>
              <div className="vr d-none d-sm-block" />
              {/* Period filter — disabled when custom date range is active */}
              <ButtonGroup>
                {PERIODS.map(p => (
                  <Button
                    key={p.key}
                    variant={!fromDate && !toDate && period === p.key ? 'primary' : 'outline-primary'}
                    size="sm"
                    onClick={() => { setPeriod(p.key); setFromDate(null); setToDate(null) }}
                  >
                    {p.label}
                  </Button>
                ))}
              </ButtonGroup>
            </div>
          </div>

          {/* Date range filter */}
          <div className="d-flex align-items-center gap-2 flex-wrap mb-3 justify-content-end">
            <span className="fs-sm text-muted">ช่วงวันที่:</span>
            <div style={{ width: 120 }}>
              <DatePicker
                selected={fromDate}
                onChange={d => setFromDate(d)}
                selectsStart
                startDate={fromDate}
                endDate={toDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="เริ่มวันที่"
                className="form-control form-control-sm"
              />
            </div>
            <span className="text-muted">–</span>
            <div style={{ width: 120 }}>
              <DatePicker
                selected={toDate}
                onChange={d => setToDate(d)}
                selectsEnd
                startDate={fromDate}
                endDate={toDate}
                minDate={fromDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="ถึงวันที่"
                className="form-control form-control-sm"
              />
            </div>
            {(fromDate || toDate) && (
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => { setFromDate(null); setToDate(null) }}
              >
                <i className="ri-close-line me-1" />ล้าง
              </Button>
            )}
            {fromDate && toDate && (
              <span className="fs-sm text-primary fw-semibold">
                <i className="ri-calendar-check-line me-1" />กำลังแสดงช่วงที่กำหนด
              </span>
            )}
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {/* Quick actions */}
          <Row className="g-3 mb-4 justify-content-center">
            <Col xs={10} sm={5} md={4} lg={3}>
              <button
                type="button"
                className="w-100 border-0 rounded-3 p-0 text-start"
                style={{
                  background: 'linear-gradient(135deg,#7c3aed 0%,#5b21b6 100%)',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(124,58,237,.35)',
                  transition: 'transform .15s, box-shadow .15s',
                  outline: 'none',
                }}
                onClick={() => navigate('/bank/accounts')}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(124,58,237,.45)' }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 12px rgba(124,58,237,.35)' }}
                onMouseDown={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(124,58,237,.3)' }}
                onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(124,58,237,.45)' }}
              >
                <div className="d-flex align-items-center gap-3 px-4 py-3">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                    style={{ width: 48, height: 48, fontSize: 24, background: 'rgba(255,255,255,.2)', color: '#fff' }}
                  >
                    <i className="ri-bank-card-line" />
                  </div>
                  <div>
                    <div className="fw-bold text-white" style={{ fontSize: 15 }}>บัญชีนักเรียน</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,.75)' }}>ดู / เปิด / ปิดบัญชี</div>
                  </div>
                  <i className="ri-arrow-right-line ms-auto text-white opacity-75" style={{ fontSize: 18 }} />
                </div>
              </button>
            </Col>
            <Col xs={10} sm={5} md={4} lg={3}>
              <button
                type="button"
                className="w-100 border-0 rounded-3 p-0 text-start"
                style={{
                  background: 'linear-gradient(135deg,#f59e0b 0%,#d97706 100%)',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(245,158,11,.35)',
                  transition: 'transform .15s, box-shadow .15s',
                  outline: 'none',
                }}
                onClick={() => navigate('/bank/transactions')}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(245,158,11,.45)' }}
                onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 12px rgba(245,158,11,.35)' }}
                onMouseDown={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 6px rgba(245,158,11,.3)' }}
                onMouseUp={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(245,158,11,.45)' }}
              >
                <div className="d-flex align-items-center gap-3 px-4 py-3">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                    style={{ width: 48, height: 48, fontSize: 24, background: 'rgba(255,255,255,.2)', color: '#fff' }}
                  >
                    <i className="ri-exchange-funds-line" />
                  </div>
                  <div>
                    <div className="fw-bold text-white" style={{ fontSize: 15 }}>บันทึกรายการ</div>
                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,.75)' }}>ฝาก / ถอนเงิน</div>
                  </div>
                  <i className="ri-arrow-right-line ms-auto text-white opacity-75" style={{ fontSize: 18 }} />
                </div>
              </button>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
          ) : (
            <>
              {/* Summary cards */}
              <Row className="g-3 mb-4">
                <Col xs={6} xl={3}>
                  <Card bg={cardBg} className="card-one">
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fs-sm text-secondary">ยอดคงเหลือทั้งหมด</span>
                        <Badge bg="primary" pill><i className="ri-wallet-3-line" /></Badge>
                      </div>
                      <h4 className="card-value mb-0 text-primary">฿{fmt(data?.total_balance)}</h4>
                      <small className="text-muted">
                        {data?.active_accounts || 0} บัญชีที่เปิดอยู่
                        {gender ? ` · ${GENDERS.find(g => g.key === gender)?.label}` : ''}
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} xl={3}>
                  <Card bg={cardBg} className="card-one">
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fs-sm text-secondary">ยอดฝากรวม</span>
                        <Badge bg="success" pill><i className="ri-arrow-down-circle-line" /></Badge>
                      </div>
                      <h4 className="card-value mb-0 text-success">฿{fmt(data?.total_deposit)}</h4>
                      <small className="text-muted">{PERIODS.find(p => p.key === period)?.label}</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} xl={3}>
                  <Card bg={cardBg} className="card-one">
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fs-sm text-secondary">ยอดถอนรวม</span>
                        <Badge bg="danger" pill><i className="ri-arrow-up-circle-line" /></Badge>
                      </div>
                      <h4 className="card-value mb-0 text-danger">฿{fmt(data?.total_withdrawal)}</h4>
                      <small className="text-muted">{PERIODS.find(p => p.key === period)?.label}</small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs={6} xl={3}>
                  <Card bg={cardBg} className="card-one">
                    <Card.Body className="p-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <span className="fs-sm text-secondary">ยอดสุทธิ (ฝาก-ถอน)</span>
                        <Badge bg="info" pill><i className="ri-exchange-funds-line" /></Badge>
                      </div>
                      <h4 className="card-value mb-0 text-info">
                        ฿{fmt((data?.total_deposit || 0) - (data?.total_withdrawal || 0))}
                      </h4>
                      <small className="text-muted">{PERIODS.find(p => p.key === period)?.label}</small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Charts */}
              <Row className="g-3">
                <Col xl={8}>
                  <Card bg={cardBg} className="card-one h-100">
                    <Card.Header className="pb-0">
                      <Card.Title as="h6" className="mb-0">ยอดฝาก-ถอน</Card.Title>
                    </Card.Header>
                    <Card.Body>
                      {barChart.series[0].data.length > 0 ? (
                        <ReactApexChart
                          options={barChart.options}
                          series={barChart.series}
                          type="bar"
                          height={300}
                        />
                      ) : (
                        <div className="text-center text-muted py-5">ไม่มีข้อมูลในช่วงเวลานี้</div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
                <Col xl={4}>
                  <Card bg={cardBg} className="card-one h-100">
                    <Card.Header className="pb-0">
                      <Card.Title as="h6" className="mb-0">สัดส่วนฝาก/ถอน</Card.Title>
                    </Card.Header>
                    <Card.Body className="d-flex align-items-center justify-content-center">
                      {(parseFloat(data?.total_deposit || 0) + parseFloat(data?.total_withdrawal || 0)) > 0 ? (
                        <ReactApexChart
                          options={donutChart.options}
                          series={donutChart.series}
                          type="donut"
                          height={300}
                        />
                      ) : (
                        <div className="text-center text-muted py-5">ไม่มีข้อมูลในช่วงเวลานี้</div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </Container>
      </div>
      <Footer />
    </>
  )
}
